class CrestlanePredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.panel = this.querySelector('[data-cl-search-panel]');
    this.status = this.querySelector('[data-cl-search-status]');
    this.clearButton = this.querySelector('[data-cl-search-clear]');
    if (!this.input || !this.form || !this.panel) return;
    this.events = new AbortController();
    this.cache = new Map();
    this.sequence = 0;
    this.activeIndex = -1;
    const options = { signal: this.events.signal };
    this.clearButton.hidden = !this.input.value;
    this.input.addEventListener('input', event => {
      this.clearButton.hidden = !this.input.value;
      if (!event.isComposing) this.schedule();
    }, options);
    this.input.addEventListener('compositionstart', () => this.close(), options);
    this.input.addEventListener('compositionend', () => this.schedule(), options);
    this.input.addEventListener('focus', () => this.schedule(), options);
    this.input.addEventListener('keydown', event => this.onKeydown(event), options);
    this.clearButton.addEventListener('click', () => {
      this.input.value = '';
      this.clearButton.hidden = true;
      this.close();
      this.input.focus({ preventScroll: true });
    }, options);
    this.form.addEventListener('submit', event => {
      if (!this.query()) { event.preventDefault(); this.input.focus(); return; }
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview && !this.form.elements.namedItem('preview_theme_id')) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = 'preview_theme_id'; input.value = preview;
        this.form.append(input);
      }
      this.close();
    }, options);
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) this.close();
    }, options);
    this.panel.addEventListener('pointerdown', event => {
      if (event.target.closest('[data-cl-search-option]') && event.pointerType !== 'touch') event.preventDefault();
    }, options);
    document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, options);
    window.addEventListener('resize', () => this.queuePosition(), options);
    window.addEventListener('scroll', () => this.queuePosition(), { ...options, passive: true });
    window.visualViewport?.addEventListener('resize', () => this.queuePosition(), options);
    window.visualViewport?.addEventListener('scroll', () => this.queuePosition(), options);
    this.resizeObserver = new ResizeObserver(() => this.queuePosition());
    this.resizeObserver.observe(this.form);
  }

  disconnectedCallback() {
    this.close();
    this.events?.abort();
    this.events = null;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.positionFrame);
  }

  query() { return this.input.value.trim().replace(/\s+/g, ' '); }

  schedule() {
    clearTimeout(this.timer);
    this.request?.abort();
    const sequence = ++this.sequence;
    const query = this.query();
    this.hide();
    if (query.length < 2) { this.status.textContent = ''; return; }
    this.timer = setTimeout(() => this.search(query, sequence), 180);
  }

  async search(query, sequence) {
    if (sequence !== this.sequence || !this.contains(document.activeElement)) return;
    const cached = this.cache.get(query);
    if (cached && Date.now() - cached.time < 30000) { this.render(cached.html); return; }
    this.message(this.dataset.loading, false);
    this.input.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.loading;
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), 8000);
    const url = new URL(this.dataset.endpoint, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('section_id', 'cl-predictive-search');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search unavailable');
      const html = await response.text();
      if (sequence !== this.sequence || query !== this.query() || !this.contains(document.activeElement)) return;
      this.render(html);
      this.cache.set(query, { html, time: Date.now() });
      if (this.cache.size > 12) this.cache.delete(this.cache.keys().next().value);
    } catch {
      if (sequence === this.sequence && query === this.query() && this.contains(document.activeElement)) {
        this.message(this.dataset.error, true);
        this.status.textContent = this.dataset.error;
      }
    } finally {
      clearTimeout(timeout);
      if (sequence === this.sequence) this.input.removeAttribute('aria-busy');
    }
  }

  render(html) {
    const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cl-predictive-content]');
    if (!content?.querySelector('[data-cl-search-options]')) throw new Error('Missing search results');
    this.panel.replaceChildren(content);
    this.prepareOptions();
    this.status.textContent = content.dataset.resultStatus;
    this.open();
  }

  message(text, includeSearch) {
    const p = document.createElement('p');
    p.className = 'cl-search-message'; p.textContent = text;
    this.panel.replaceChildren(p);
    if (includeSearch) {
      const list = document.createElement('ul');
      list.className = 'cl-search-options'; list.setAttribute('role', 'listbox'); list.setAttribute('aria-label', this.dataset.viewAll); list.dataset.clSearchOptions = '';
      const item = document.createElement('li'); item.setAttribute('role', 'presentation');
      const link = document.createElement('a');
      const url = new URL(this.form.action); url.searchParams.set('q', this.query()); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last');
      link.href = url.toString(); link.className = 'cl-search-view-all'; link.textContent = this.dataset.viewAll; link.setAttribute('role', 'option'); link.setAttribute('aria-selected', 'false'); link.tabIndex = -1; link.dataset.clSearchOption = '';
      item.append(link); list.append(item); this.panel.append(list); this.prepareOptions();
    }
    this.open();
  }

  prepareOptions() {
    const list = this.panel.querySelector('[data-cl-search-options]');
    if (list) list.id = this.input.getAttribute('aria-controls');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    this.panel.querySelectorAll('[data-cl-search-option]').forEach((option, index) => {
      option.id = `${this.input.id}-option-${index}`;
      if (preview) { const url = new URL(option.href); url.searchParams.set('preview_theme_id', preview); option.href = url.toString(); }
    });
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') { this.close(); event.preventDefault(); event.stopPropagation(); return; }
    const options = [...this.panel.querySelectorAll('[data-cl-search-option]')];
    if (this.panel.hidden || !options.length) {
      if (event.key === 'ArrowDown' && this.query().length >= 2) { event.preventDefault(); this.schedule(); }
      return;
    }
    let next = this.activeIndex;
    if (event.key === 'ArrowDown') next = (next + 1) % options.length;
    else if (event.key === 'ArrowUp') next = next <= 0 ? options.length - 1 : next - 1;
    else if (event.key === 'Home' && next >= 0) next = 0;
    else if (event.key === 'End' && next >= 0) next = options.length - 1;
    else if (event.key === 'Enter' && next >= 0) { event.preventDefault(); options[next].click(); return; }
    else return;
    event.preventDefault();
    this.activeIndex = next;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({ block: 'nearest' });
  }

  queuePosition() {
    if (this.panel.hidden || this.positionFrame) return;
    this.positionFrame = requestAnimationFrame(() => { this.positionFrame = 0; this.position(); });
  }

  position() {
    const viewport = window.visualViewport;
    const bottom = (viewport?.height || window.innerHeight) + (viewport?.offsetTop || 0);
    this.style.setProperty('--cl-search-space', `${Math.max(80, Math.floor(bottom - this.form.getBoundingClientRect().bottom - 20))}px`);
  }

  open() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); this.position(); }

  hide() {
    this.panel.hidden = true;
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.removeAttribute('aria-busy');
  }

  close() {
    clearTimeout(this.timer);
    this.sequence = (this.sequence || 0) + 1;
    this.request?.abort();
    if (this.panel && this.input) this.hide();
    if (this.status) this.status.textContent = '';
  }
}

if (!customElements.get('crestlane-predictive-search')) customElements.define('crestlane-predictive-search', CrestlanePredictiveSearch);
